if (!customElements.get('swiper-media-gallery')) {
  customElements.define(
    'swiper-media-gallery',
    class SwiperMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.mediaCount = parseInt(this.dataset.mediaCount);
        this.swiperMain = null;
        this.swiperThumbs = null;
        this.initialized = false;
        this.isLightboxActive = false;
        this.currentIndex = 0;
        this.placeholder = null;
        this.isOpeningLightbox = false;
      }

      connectedCallback() {
        if (typeof Swiper === 'undefined') {
          setTimeout(() => this.connectedCallback(), 100);
          return;
        }

        if (this.initialized) return;
        this.initialized = true;

        requestAnimationFrame(() => {
          this.initSwipers();
          this.bindEvents();
          this.bindLightboxEvents();
          this.initCustomVideoControls();
        });
      }


      initSwipers() {
        if (this.mediaCount > 1) {
          const thumbsEl = this.querySelector('#GalleryThumbnails-' + this.sectionId);
          if (thumbsEl) {
            this.swiperThumbs = new Swiper(thumbsEl, {
              spaceBetween: 12,
              slidesPerView: 'auto',
              freeMode: true,
              watchSlidesProgress: true,
              slideToClickedSlide: true,
              speed: 600,
              breakpoints: {
                320: { spaceBetween: 8 },
                750: { spaceBetween: 10 },
                990: { spaceBetween: 12 },
              },
            });
          }
        }

        const mainEl = this.querySelector('#GalleryViewer-' + this.sectionId);
        if (mainEl) {
          this.swiperMain = new Swiper(mainEl, {
            loop: this.mediaCount > 1,
            spaceBetween: 0,
            speed: 1000,
            effect: 'slide',
            navigation: {
              nextEl: '.swiper-button-main-next',
              prevEl: '.swiper-button-main-prev',
              disabledClass: 'swiper-button-disabled',
            },
            thumbs: {
              swiper: this.swiperThumbs,
            },
            on: {
              slideChange: () => {
                this.handleSlideChange();
              },
              transitionEnd: () => {
                this.handleMediaAutoplay();
              },
            },
          });

          this.addKeyboardNavigation();
        }
      }

      getVisibleEdgeIndices() {
        if (!this.swiperThumbs) return { leftmost: -1, rightmost: -1 };
        
        const container = this.swiperThumbs.el;
        const containerRect = container.getBoundingClientRect();
        const slides = this.swiperThumbs.slides;
        
        let leftmost = -1;
        let rightmost = -1;
        
        for (let i = 0; i < slides.length; i++) {
          const slideRect = slides[i].getBoundingClientRect();
          
          const visibleStart = Math.max(containerRect.left, slideRect.left);
          const visibleEnd = Math.min(containerRect.right, slideRect.right);
          const visibleWidth = Math.max(0, visibleEnd - visibleStart);
          
          const visibilityRatio = slideRect.width > 0 ? visibleWidth / slideRect.width : 0;
          
          if (visibilityRatio >= 0.25) {
            if (leftmost === -1) leftmost = i;
            rightmost = i;
          }
        }
        
        return { leftmost, rightmost };
      }

      bindEvents() {
        const thumbnails = this.querySelectorAll('.swiper-thumb-slide');
        thumbnails.forEach((thumb, index) => {
          const button = thumb.querySelector('button');
          if (button) {
            button.addEventListener('click', (e) => {
              e.preventDefault();
              if (!this.swiperMain || !this.swiperThumbs) return;

              const clickedIndex = index;
              const { leftmost, rightmost } = this.getVisibleEdgeIndices();
              const totalSlides = this.swiperThumbs.slides.length;
              
              const isLeftEdge = clickedIndex === leftmost;
              const isRightEdge = clickedIndex === rightmost;
              const isGlobalFirst = clickedIndex === 0;
              const isGlobalLast = clickedIndex === totalSlides - 1;

              this.swiperMain.slideToLoop(clickedIndex, 1000);

              if (isLeftEdge && !isGlobalFirst) {
                this.swiperThumbs.slidePrev(500);
              } else if (isRightEdge && !isGlobalLast) {
                this.swiperThumbs.slideNext(500);
              }
            });
          }
        });
      }

      bindLightboxEvents() {
        const triggers = this.querySelectorAll('[data-lightbox-trigger]');
        triggers.forEach(trigger => {
          trigger.addEventListener('click', (e) => {
            if (this.isLightboxActive) {
              return;
            }
            
            if (e.target.closest('[data-video-play]') || 
                e.target.closest('[data-video-mute]')) {
              return;
            }
            
            e.preventDefault();
            e.stopPropagation();
            const slideEl = trigger.closest('.swiper-slide');
            const index = parseInt(slideEl.dataset.mediaIndex) || 0;
            this.openLightbox(index);
          });
        });

        const closeBtn = this.querySelector('[data-lightbox-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeLightbox();
          });
        }

        this.escapeHandler = (e) => {
          if (e.key === 'Escape' && this.isLightboxActive) {
            this.closeLightbox();
          }
        };
        document.addEventListener('keydown', this.escapeHandler);

        const container = this.querySelector('.productBanner-container');
        if (container) {
          container.addEventListener('click', (e) => {
            if (this.isLightboxActive && e.target === container) {
              this.closeLightbox();
            }
          });
        }
      }

      openLightbox(index = null) {
        if (this.isLightboxActive) return;

        this.lastScrollY = window.scrollY;

        const container = this.querySelector('.productBanner-container');
        const backdrop = this.querySelector('.window-back');
        
        if (!container || !backdrop) return;

        const containerHeight = container.offsetHeight;
        
        if (!this.placeholder) {
          this.placeholder = document.createElement('div');
          this.placeholder.className = 'lightbox-placeholder';
          this.placeholder.style.height = `${containerHeight}px`;
          this.placeholder.style.width = '100%';
          this.placeholder.style.visibility = 'hidden';
          this.placeholder.style.pointerEvents = 'none';
          container.parentNode.insertBefore(this.placeholder, container);
        } else {
          this.placeholder.style.height = `${containerHeight}px`;
          this.placeholder.style.display = 'block';
        }

        this.isLightboxActive = true;
        this.isOpeningLightbox = true;

        backdrop.classList.add('is-visible');
        this.classList.add('lightbox-mode');
        container.classList.add('active');

        if (index !== null && this.swiperMain) {
          this.swiperMain.slideToLoop(index, 0);
        }

        this.updateCounter();

        setTimeout(() => {
          if (this.swiperMain) {
            this.swiperMain.update();
          }
          this.isOpeningLightbox = false;
        }, 350);
      }

      closeLightbox() {
        if (!this.isLightboxActive) return;

        this.isLightboxActive = false;
        const container = this.querySelector('.productBanner-container');
        const backdrop = this.querySelector('.window-back');
        
        if (!container || !backdrop) return;

        container.classList.remove('active');
        backdrop.classList.remove('is-visible');
        this.classList.remove('lightbox-mode');

        if (this.placeholder) {
          this.placeholder.style.display = 'none';
        }
        
        this.style.height = '';

        requestAnimationFrame(() => {
          window.scrollTo(0, this.lastScrollY);
        });

        this.pauseAllMedia();

        setTimeout(() => {
          if (this.swiperMain) {
            this.swiperMain.update();
          }
        }, 350);
      }

      updateCounter() {
        if (!this.swiperMain) return;
        const current = this.swiperMain.realIndex + 1;
        const currentEl = this.querySelector('.productBanner-counter-current');
        if (currentEl) {
          currentEl.textContent = current;
        }
      }

      pauseAllMedia() {
        this.querySelectorAll('video').forEach(video => {
          video.pause();
        });
        this.querySelectorAll('iframe').forEach(iframe => {
          try {
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            iframe.contentWindow.postMessage('{"method":"pause"}', '*');
          } catch (e) {}
        });
      }

      addKeyboardNavigation() {
        const mainEl = this.querySelector('.swiper-main');
        if (!mainEl) return;

        mainEl.setAttribute('tabindex', '0');
        
        mainEl.addEventListener('keydown', (e) => {
          if (!this.swiperMain) return;

          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.swiperMain.slidePrev();
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.swiperMain.slideNext();
          }
        });
      }

      handleSlideChange() {
        const nextSlide = this.swiperMain?.slides[this.swiperMain.activeIndex];
        const nextVideo = nextSlide?.querySelector('.custom-video-container video');
        const isNextVideoAutoplay = nextVideo?.hasAttribute('autoplay');
        
        if (nextVideo && isNextVideoAutoplay && !this.isOpeningLightbox) {
          const hasPlayed = nextVideo.dataset.hasPlayed === 'true';
          if (!hasPlayed) {
            nextVideo.currentTime = 0;
            nextVideo.dataset.hasPlayed = 'true';
          }
          nextVideo.play().catch(() => {});
        }
        
        if (!this.isOpeningLightbox) {
          this.querySelectorAll('video').forEach(video => {
            if (video !== nextVideo) {
              video.pause();
              const videoContainer = video.closest('.custom-video-container');
              const playBtn = videoContainer?.querySelector('[data-video-play]');
              if (playBtn) {
                playBtn.setAttribute('data-playing', 'true');
              }
            }
          });
          this.querySelectorAll('iframe').forEach(iframe => {
            try {
              iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
              iframe.contentWindow.postMessage('{"method":"pause"}', '*');
            } catch (e) {}
          });
        }
        
        if (this.swiperMain) {
          const nextPlayBtn = nextSlide?.querySelector('.custom-video-container [data-video-play]');
          if (nextVideo && nextPlayBtn && isNextVideoAutoplay) {
            nextPlayBtn.setAttribute('data-playing', 'true');
          }
        }
        
        this.updateCounter();
        
        if (this.swiperThumbs && this.swiperMain) {
          const realIndex = this.swiperMain.realIndex;
          const { leftmost, rightmost } = this.getVisibleEdgeIndices();
          
          if (realIndex < leftmost || realIndex > rightmost) {
            this.swiperThumbs.slideTo(realIndex, 500);
          }
        }
      }

      handleMediaAutoplay() {
        if (!this.swiperMain) return;

        const activeSlide = this.swiperMain.slides[this.swiperMain.activeIndex];
        const video = activeSlide?.querySelector('video');

        if (video) {
          const playBtn = activeSlide?.querySelector('[data-video-play]');
          
          if (playBtn) {
            playBtn.setAttribute('data-playing', 'true');
          }
          
          if (video.paused) {
            video.play().catch((err) => {
              if (playBtn) {
                playBtn.setAttribute('data-playing', 'false');
              }
              console.log('Video autoplay error:', err);
            });
          }
        }
      }

      initCustomVideoControls() {
        const videoContainers = this.querySelectorAll('.custom-video-container');
        
        videoContainers.forEach(container => {
          const video = container.querySelector('video');
          const muteBtnNormal = this.querySelector('.custom-video-mute--normal');
          const muteBtnLightbox = container.querySelector('[data-video-mute-lightbox]');
          const playBtn = container.querySelector('[data-video-play]');
          const progressBar = container.querySelector('[data-video-progress-bar]');
          const progressFill = container.querySelector('[data-video-progress-fill]');
          const progressInput = container.querySelector('[data-video-progress-input]');
          const currentTimeEl = container.querySelector('[data-video-current-time]');
          const durationEl = container.querySelector('[data-video-duration]');
          
          if (!video) return;

          muteBtnNormal?.setAttribute('data-muted', video.muted ? 'true' : 'false');
          muteBtnLightbox?.setAttribute('data-muted', video.muted ? 'true' : 'false');
          playBtn?.setAttribute('data-playing', !video.paused ? 'true' : 'false');

          const toggleMute = (e) => {
            e.preventDefault();
            e.stopPropagation();
            video.muted = !video.muted;
            const muted = video.muted ? 'true' : 'false';
            muteBtnNormal?.setAttribute('data-muted', muted);
            muteBtnLightbox?.setAttribute('data-muted', muted);
          };

          muteBtnNormal?.addEventListener('click', toggleMute);
          muteBtnLightbox?.addEventListener('click', toggleMute);

          const togglePlayPause = () => {
            if (video.paused) {
              video.play();
              playBtn?.setAttribute('data-playing', 'true');
            } else {
              video.pause();
              playBtn?.setAttribute('data-playing', 'false');
            }
          };

          playBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            togglePlayPause();
          });

          const handleVideoClick = (e) => {
            if (!this.isLightboxActive) {
              return;
            }
            
            if (e.target.closest('[data-video-play]') || 
                e.target.closest('[data-video-mute-lightbox]') ||
                e.target.closest('.custom-video-progress-bar')) {
              return;
            }
            
            e.preventDefault();
            e.stopPropagation();
            togglePlayPause();
          };

          video.addEventListener('click', handleVideoClick.bind(this));

          video.addEventListener('play', () => {
            playBtn?.setAttribute('data-playing', 'true');
          });

          video.addEventListener('pause', () => {
            playBtn?.setAttribute('data-playing', 'false');
          });

          video.addEventListener('ended', () => {
            playBtn?.setAttribute('data-playing', 'false');
          });

          const formatTime = (seconds) => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          };

          const updateDuration = () => {
            if (durationEl && video.duration && !isNaN(video.duration) && video.duration !== Infinity) {
              durationEl.textContent = formatTime(video.duration);
            }
          };

          if (video.readyState >= 1) {
            updateDuration();
          }

          video.addEventListener('loadedmetadata', updateDuration);
          video.addEventListener('durationchange', updateDuration);

          video.addEventListener('timeupdate', () => {
            if (!progressFill || !progressInput) return;
            
            const percent = (video.currentTime / video.duration) * 100;
            progressFill.style.width = `${percent}%`;
            progressInput.value = percent;
            
            if (currentTimeEl) {
              currentTimeEl.textContent = formatTime(video.currentTime);
            }
          });

          progressInput?.addEventListener('input', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const percent = parseFloat(e.target.value);
            const time = (percent / 100) * video.duration;
            
            video.currentTime = time;
            
            if (progressFill) {
              progressFill.style.width = `${percent}%`;
            }
          });

          progressInput?.addEventListener('click', (e) => {
            e.stopPropagation();
          });
        });
      }

      disconnectedCallback() {
        if (this.escapeHandler) {
          document.removeEventListener('keydown', this.escapeHandler);
        }
        
        this.swiperMain?.destroy(true, true);
        this.swiperThumbs?.destroy(true, true);
        this.initialized = false;
      }
    }
  );
}